/**
 * @file: App.js
 * @status: Unified Sovereign Gateway (V12.3.0) - GLOBAL & FINANCIAL INTEGRATION
 * @authority: @CEO - Abdullah Ahmed Al-Zahrani
 * @version: 2026.04.26
 * @description: الهيكل المعماري الكامل؛ دمج نظام الفواتير السيادية، محرك اللغات i18n، ونظام الرقابة اللحظية للهوية.
 * [تم تحديث المسارات لتطابق مجلدات pages و components بختم @CEO]
 */

import React, { Suspense, useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next'; // استدعاء محرك اللغات

// --- [ استدعاء المكونات الاستراتيجية المحصنة من مجلد components ] ---
import Navbar from './components/Navbar';
import NotificationCenter from './components/NotificationCenter';

// --- [ استدعاء الصفحات السيادية من مجلد pages ] ---
import Dashboard from './pages/Dashboard';
import ControlCenter from './pages/ControlCenter';
import Inventory from './pages/Inventory';
import ProductionTracker from './pages/ProductionTracker';
import InvestorDashboard from './pages/InvestorDashboard';
import Financing from './pages/Financing';
import Invoices from './pages/Invoices'; 

// التنسيق العام السيادي لشركة ضخامة
import './index.css';
import { message } from 'antd';

function App() {
  const { i18n } = useTranslation();
  
  // بروتوكول التحقق من حالة المصادقة السيادية اللحظي
  const [isAuth, setIsAuth] = useState(localStorage.getItem('huger_authenticated') === 'true');
  const [userRole, setUserRole] = useState(localStorage.getItem('huger_user_role'));

  // --- [ محرك الرقابة اللحظية للهوية ] ---
  useEffect(() => {
    const syncSovereignAuth = () => {
      const authStatus = localStorage.getItem('huger_authenticated') === 'true';
      const currentRole = localStorage.getItem('huger_user_role');
      
      if (authStatus !== isAuth || currentRole !== userRole) {
        setIsAuth(authStatus);
        setUserRole(currentRole);
      }
    };
    
    window.addEventListener('storage', syncSovereignAuth);
    const authInterval = setInterval(syncSovereignAuth, 1000);

    return () => {
      window.removeEventListener('storage', syncSovereignAuth);
      clearInterval(authInterval);
    };
  }, [isAuth, userRole]);

  // ضبط اتجاه الصفحة (RTL/LTR) بناءً على اللغة المعتمدة
  useEffect(() => {
    document.dir = i18n.language === 'ar' ? 'rtl' : 'ltr';
  }, [i18n.language]);

  // --- [ مكون حماية المسارات (The Sovereign Guard) ] ---
  const ProtectedRoute = ({ children, allowedRoles }) => {
    if (!isAuth) {
      return <Navigate to="/" replace />;
    }
    if (allowedRoles && !allowedRoles.includes(userRole)) {
      message.warning("تنبيه سيادي: رتبتك لا تسمح بالولوج لهذا القطاع.");
      return <Navigate to="/dashboard" replace />;
    }
    return children;
  };

  return (
    <Router>
      <div className={`huger-digital-empire min-h-screen bg-[#050B14] flex flex-col font-sans overflow-x-hidden w-full relative ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>
        
        {/* 🛰️ [الرادار الموحد] يظهر فقط بعد عبور بوابة الرياض */}
        {isAuth && (
          <header className="sticky top-0 z-[1000] w-full flex flex-col shadow-2xl transition-all duration-500">
            <Navbar />
            <div className="w-full bg-[#0A192F]/40 backdrop-blur-2xl border-b border-white/5">
                <NotificationCenter />
            </div>
          </header>
        )}

        {/* 🔒 [منطقة العمليات المركزية] */}
        <main className="flex-grow w-full max-w-full relative z-10 container mx-auto">
          <Suspense fallback={
            <div className="flex flex-col justify-center items-center h-screen bg-[#050B14]">
                <div className="relative mb-10">
                   <div className="absolute inset-0 bg-[#F57C00] rounded-full blur-[60px] opacity-20 animate-pulse"></div>
                   <div className="w-24 h-24 border-2 border-white/5 border-t-[#F57C00] rounded-full animate-spin"></div>
                </div>
                <p className="text-[#F57C00] font-black uppercase italic tracking-[0.6em] animate-pulse text-xs">
                  HUGER Sovereign Syncing...
                </p>
            </div>
          }>
            <Routes>
              {/* 🇸🇦 بوابة الوصول الوطنية */}
              <Route path="/" element={
                isAuth ? <Navigate to="/dashboard" replace /> : <ControlCenter />
              } />
              
              {/* 📊 الرادار الاستراتيجي الرئيسي */}
              <Route path="/dashboard" element={
                <ProtectedRoute allowedRoles={['CEO', 'Manager', 'Investor', 'Staff']}>
                  <Dashboard />
                </ProtectedRoute>
              } />
              
              {/* 🛡️ مركز التحكم (CEO Only) */}
              <Route path="/control-center" element={
                <ProtectedRoute allowedRoles={['CEO']}>
                  <ControlCenter />
                </ProtectedRoute>
              } />

              {/* 📦 مخزن الابتكار (3000+ أصل) */}
              <Route path="/inventory" element={
                <ProtectedRoute allowedRoles={['CEO', 'Manager']}>
                  <Inventory />
                </ProtectedRoute>
              } />
              
              {/* 🏭 رادار الإنتاج الصناعي */}
              <Route path="/production" element={
                <ProtectedRoute allowedRoles={['CEO', 'Manager', 'Staff']}>
                  <ProductionTracker />
                </ProtectedRoute>
              } />
              
              {/* 📈 بوابة المستثمر */}
              <Route path="/investors" element={
                <ProtectedRoute allowedRoles={['CEO', 'Investor']}>
                  <InvestorDashboard />
                </ProtectedRoute>
              } />
              
              {/* 💳 البوابة التمويلية (Fintech) */}
              <Route path="/financing" element={
                <ProtectedRoute allowedRoles={['CEO', 'Manager']}>
                  <Financing />
                </ProtectedRoute>
              } />

              {/* 📑 مركز الفواتير السيادية (جديد) */}
              <Route path="/invoices" element={
                <ProtectedRoute allowedRoles={['CEO', 'Manager']}>
                  <Invoices />
                </ProtectedRoute>
              } />
              
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </main>

        {/* 🔒 تذييل المنظومة السيادية */}
        <footer className="bg-black/80 border-t border-white/5 py-12 w-full relative z-20 backdrop-blur-3xl">
          <div className="max-w-7xl mx-auto px-8 text-center">
            <div className="flex justify-center items-center gap-6 mb-6 text-white/50 font-black italic text-lg uppercase tracking-[0.4em]">
              <span>ضخامة</span>
              <div className="w-2 h-2 bg-[#F57C00] rounded-full shadow-[0_0_15px_#F57C00] animate-pulse"></div>
              <span>HUGER</span>
            </div>
            <p className="text-gray-600 text-[10px] font-black uppercase tracking-[0.8em] italic">
              Sovereign Industrial Infrastructure • 2026 • Verified by @CEO
            </p>
          </div>
        </footer>

        {/* التأثيرات الخلفية السيادية */}
        <div className="fixed top-0 left-0 w-full h-full pointer-events-none opacity-30 z-0 overflow-hidden">
            <div className="absolute top-[-10%] right-[-10%] w-[50%] h-[50%] bg-blue-900/20 rounded-full blur-[160px] animate-pulse"></div>
            <div className="absolute bottom-[-10%] left-[-10%] w-[50%] h-[50%] bg-[#F57C00]/10 rounded-full blur-[160px] animate-pulse"></div>
        </div>
      </div>
    </Router>
  );
}

export default App;